// Hardcoded guide listings for the CAT-993 docs nav prototype.

export type GuideStatus = "draft" | "in-review" | "published" | "archived"

export type Industry = "retail" | "real-estate" | "municipalities"

export interface IndustryMeta {
  id: Industry
  label: string
  description: string
}

// Deliberately non-exhaustive: industries exist to aid discovery (and SEO),
// not to classify every guide. Add one only when there's content to back it.
export const INDUSTRIES: IndustryMeta[] = [
  {
    id: "retail",
    label: "Retail",
    description:
      "Retail analytics on real-world visits: store performance, brand benchmarking, and shopper behavior at the site and network level.",
  },
  {
    id: "real-estate",
    label: "Real Estate",
    description:
      "Visitation data across commercial, retail, office, and residential real estate, covering site evaluation, tenant mix, and portfolio decisions.",
  },
  {
    id: "municipalities",
    label: "Municipalities",
    description:
      "Foot-traffic evidence for municipalities and public agencies: city-center vitality, mobility patterns, tourism, and planning decisions.",
  },
]

export function getIndustry(id: string): IndustryMeta | undefined {
  return INDUSTRIES.find((i) => i.id === id)
}

/**
 * Themes are the primary browse dimension for how-to guides: broad practice
 * areas a guide belongs to. A wide concept like site selection is a theme —
 * several guides (void analysis, cannibalization, ...) are parts of it.
 */
export type Theme =
  | "site-selection"
  | "retail-analytics"
  | "urban-development"

export interface ThemeMeta {
  id: Theme
  label: string
  description: string
}

export const THEMES: ThemeMeta[] = [
  {
    id: "site-selection",
    label: "Site Selection",
    description:
      "Everything around choosing and evaluating locations: candidate scoring, void analysis, cannibalization.",
  },
  {
    id: "retail-analytics",
    label: "Retail Analytics",
    description:
      "Store and brand performance: benchmarking, profiling, shopper journeys, portfolio decisions.",
  },
  {
    id: "urban-development",
    label: "Urban Development",
    description:
      "How districts and areas function and evolve: vitality, activity mix, development tracking.",
  },
]

export function getTheme(id: string): ThemeMeta | undefined {
  return THEMES.find((t) => t.id === id)
}

export type ExampleTool =
  | "console"
  | "api"
  | "python"
  | "ai-mcp"
  | "insights"
  | "no-code"
  | "node"
  | "bash"

export const EXAMPLE_TOOL_LABEL: Record<ExampleTool, string> = {
  console: "Console",
  api: "API",
  python: "Python",
  "ai-mcp": "AI",
  insights: "Insights",
  "no-code": "No-code",
  node: "Node.js",
  bash: "Shell",
}

export interface Example {
  /** Slug local to the parent guide. */
  slug: string
  title: string
  description: string
  tool: ExampleTool
  /** ISO-3 country code (e.g. "USA", "NOR"); omit for cross-country examples. */
  country?: string
  /** Free-form search keywords for finding this example from the guides index. */
  tags?: string[]
  /** Data reference names the recipe queries, rendered on the example page. */
  datasets?: string[]
}

export interface GuideEntry {
  slug: string
  title: string
  description: string
  status: GuideStatus
  industry: Industry[]
  /** Primary browse dimension for how-to guides; empty for get-started. */
  themes?: Theme[]
  locationTypes: ("place" | "area" | "way")[]
  tags: string[]
  /** Slugs of related dataset entries. */
  relatedDatasets: string[]
  /** IDs of related metric concepts. */
  relatedMetrics: string[]
  /** When true, the body of the guide is gated — anonymous visitors see a paywall card over a blurred preview. */
  locked?: boolean
  /** Concrete walk-throughs. May vary by country, tool, or approach. Empty for case studies. */
  examples?: Example[]
}

export const guides: GuideEntry[] = [
  {
    slug: "first-query",
    title: "Make your first query",
    description:
      "Get access, create an API key, and run your first query against the catalog API, with cURL, and the response shapes explained.",
    status: "published",
    industry: [],
    locationTypes: [],
    tags: ["get-started", "api", "quickstart"],
    relatedDatasets: [],
    relatedMetrics: [],
  },
  {
    slug: "match-locations",
    title: "Match your locations",
    description:
      "Bring your own list of stores or sites and match them to Pine59's location identifiers so you can query our data using your IDs.",
    status: "published",
    industry: ["retail", "real-estate"],
    locationTypes: ["place"],
    tags: ["platform", "custom-locations", "matching"],
    relatedDatasets: [],
    relatedMetrics: [],
  },
  {
    slug: "build-with-ai",
    title: "Build with AI",
    description:
      "Point Claude, Cursor, or other AI clients at the Pine59 docs and API to list datasets, query metrics, and explain results without writing glue code.",
    status: "published",
    industry: [],
    locationTypes: [],
    tags: ["get-started", "api", "ai"],
    relatedDatasets: [],
    relatedMetrics: [],
  },
  {
    slug: "bulk-export",
    title: "Export data in bulk with Python",
    description:
      "Run a query server-side as an asynchronous export job and download the results as files: create, poll, download, straight into pandas.",
    status: "published",
    industry: [],
    locationTypes: [],
    tags: ["platform", "api", "python", "export"],
    relatedDatasets: [],
    relatedMetrics: [],
  },
  {
    slug: "no-code",
    title: "Use Pine59 without code",
    description:
      "Browse the catalog, build dashboards, and download samples through Insights and Console. No SDK or API key required.",
    status: "published",
    industry: [],
    locationTypes: [],
    tags: ["get-started", "no-code", "insights", "console"],
    relatedDatasets: [],
    relatedMetrics: [],
  },
  {
    slug: "site-selection",
    title: "Site Selection",
    description:
      "Pick the best location for your next store, branch, or venue. Use foot traffic, visitor demographics, and trade-area metrics to compare candidate sites side by side and quantify their fit.",
    status: "draft",
    industry: ["retail", "real-estate"],
    themes: ["site-selection"],
    locationTypes: ["place", "area"],
    tags: ["site-selection", "expansion", "competitive-overlap"],
    relatedDatasets: [],
    relatedMetrics: ["foot-traffic", "visitor-demographics", "trade-area"],
  },
  {
    slug: "brand-comparison",
    title: "Brand Comparison",
    description:
      "Compare brands with aggregated location intelligence (scale, typical store performance, and development over time) in support of competitive intelligence and market understanding.",
    status: "published",
    industry: ["retail"],
    themes: ["retail-analytics"],
    locationTypes: ["place"],
    tags: ["competitor-intel", "benchmarking", "brand-analysis"],
    relatedDatasets: [],
    relatedMetrics: ["foot-traffic", "visitor-demographics", "visitor-profile"],
  },
  {
    slug: "store-profiling",
    title: "Store Profiling",
    description:
      "Build a complete picture of a single location: visit volume against the chain and category, weekly rhythm, who visits, and where they come from.",
    status: "published",
    industry: ["retail", "real-estate"],
    themes: ["retail-analytics", "site-selection"],
    locationTypes: ["place"],
    tags: ["competitor-intel", "site-analysis", "profiling"],
    relatedDatasets: [],
    relatedMetrics: [
      "foot-traffic",
      "popular-times",
      "visitor-demographics",
      "visitor-profile",
      "trade-area",
      "visitor-journey",
    ],
  },
  {
    slug: "void-analysis",
    title: "Void Analysis",
    description:
      "Find what a market lacks: compare an area's measured demand against references such as analog areas, competitor networks, or your own stores, then validate each gap with real movement.",
    status: "published",
    industry: ["retail", "real-estate", "municipalities"],
    themes: ["site-selection"],
    locationTypes: ["place", "area"],
    tags: ["void-analysis", "site-analysis", "expansion", "white-space", "cannibalization"],
    relatedDatasets: [],
    relatedMetrics: ["foot-traffic-vwr", "visitor-demographics-area", "visitor-profile-area", "trade-area-area", "visitor-journey"],
  },
  {
    slug: "analog-modeling",
    title: "Analog Modeling: Look-Alike Site Selection",
    description:
      "Let your own results set the criteria: profile the locations where your stores perform, then screen a market for look-alike locations you haven't entered.",
    status: "published",
    industry: ["retail", "real-estate"],
    themes: ["site-selection"],
    locationTypes: ["place", "area"],
    tags: ["analog", "site-selection", "look-alike", "expansion", "screening"],
    relatedDatasets: [],
    relatedMetrics: ["foot-traffic", "popular-times", "visitor-demographics", "visitor-profile", "trade-area"],
  },
  {
    slug: "area-identification",
    title: "Area Identification",
    description:
      "Score every area in a market on explicit criteria (activity, mix, momentum, seasonality, timing, audience, presence) and combine them into a ranked shortlist.",
    status: "published",
    industry: ["retail", "real-estate", "municipalities"],
    themes: ["site-selection", "urban-development"],
    locationTypes: ["area"],
    tags: ["area-identification", "screening", "scoring", "expansion"],
    relatedDatasets: [],
    relatedMetrics: ["foot-traffic-vwr", "visitor-demographics-area", "visitor-profile-area"],
  },
  {
    slug: "area-development-reporting",
    title: "Area Development Reporting",
    description:
      "Report on the areas you answer for, either evaluating what an investment changed or continuously measuring an existing asset, with activity, mix, timing, and audience developments against a benchmark.",
    status: "published",
    industry: ["municipalities", "real-estate"],
    themes: ["urban-development"],
    locationTypes: ["area"],
    tags: ["development-reporting", "before-after", "district", "bid"],
    relatedDatasets: [],
    relatedMetrics: ["foot-traffic-vwr", "visit-length-area", "visitor-demographics-area"],
  },
]

// Fake examples per guide. Case studies (slug starts with `case-study-`) and a
// few short / API-tagged ops guides are intentionally examples-free.
const EXAMPLES_BY_GUIDE: Record<string, Example[]> = {
  "site-selection": [
    {
      slug: "cafe-oslo-console",
      title: "Picking a café location in downtown Oslo",
      description:
        "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.",
      tool: "console",
      country: "NOR",
    },
    {
      slug: "trade-area-overlap-texas",
      title: "Quantifying retail trade-area overlap in Texas",
      description:
        "Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.",
      tool: "api",
      country: "USA",
    },
    {
      slug: "ai-assisted-comparison",
      title: "Comparing candidate sites with Claude",
      description:
        "Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur.",
      tool: "ai-mcp",
    },
  ],
  "area-development-reporting": [
    {
      slug: "us-16th-street-denver",
      title: "Reporting the 16th Street reopening in Denver",
      description:
        "The before-and-after lens on a real intervention, in bash and jq: the corridor's share of metro visits through construction and reopening, the quarterly spread against the benchmark, and the four-line report.",
      tool: "bash",
      country: "USA",
      tags: ["before-after", "downtown", "reopening", "denver"],
      datasets: [
        "neighborhood_visitation.foot_traffic_month",
        "neighborhood_visitation.census_visitor_demographics",
        "neighborhood_visitation.census_block_groups",
      ],
    },
    {
      slug: "no-grunerlokka-tracking",
      title: "Tracking Grünerløkka's district trend",
      description:
        "The continuous lens on Oslo's canonical district: 3.5 years of daily data resolved by name, the standing scorecard (the district up against a flat Oslo benchmark, reversing two soft years, visitor share at a four-year high), dwell stability, and the consortium read.",
      tool: "node",
      country: "NOR",
      tags: ["continuous", "district", "consortium", "oslo", "nordic"],
      datasets: [
        "pl_telco_nor_neighborhoods_al3.dl_telco_foot_traffic_1",
        "pl_telco_nor_neighborhoods_al3.dl_telco_visit_length_1",
      ],
    },
  ],
  "analog-modeling": [
    {
      slug: "us-candidate-malls-colorado",
      title: "Finding candidate malls for an in-mall retailer",
      description:
        "Classify a state's malls into a handful of transparent classes, join explicitly fictional internal sales, and shortlist the look-alike centers with no store yet, in Python on calendar-2025 data.",
      tool: "python",
      country: "USA",
      tags: ["analog", "site-selection", "malls", "classification", "expansion"],
      datasets: [
        "visitation_usa_places.foot_traffic_month",
        "visitation_usa_places.census_visitor_demographics",
        "visitation_usa_places.spatialai_visitor_profile",
      ],
    },
  ],
  "void-analysis": [
    {
      slug: "us-grocery-penetration-denver",
      title: "Grocery penetration block by block in Denver",
      description:
        "Trade areas times store visits, joined in DuckDB: captured visits, the leading grocer, and saturation per block group, ending in a headroom shortlist.",
      tool: "python",
      country: "USA",
      tags: ["penetration", "saturation", "trade-area", "competitive", "denver"],
      datasets: [
        "visitation_usa_places.trade_area",
        "visitation_usa_places.foot_traffic_month",
        "neighborhood_visitation.foot_traffic_month",
        "neighborhood_visitation.census_block_groups",
      ],
    },
    {
      slug: "us-sprouts-white-space-denver",
      title: "Mapping Sprouts' white space in Denver",
      description:
        "The brand white-space variant in Python: profile Sprouts' strongest catchments, screen the competitor network for look-alike areas the brand has not entered (16 candidates in Denver, 2025), then test each against measured journeys.",
      tool: "python",
      country: "USA",
      tags: ["white-space", "expansion", "grocery", "denver"],
      datasets: [
        "visitation_usa_places.places",
        "neighborhood_visitation.foot_traffic_month",
        "neighborhood_visitation.census_visitor_demographics",
        "visitation_usa_places.visitor_journey",
      ],
    },
  ],
  "area-identification": [
    {
      slug: "us-cbg-scoring",
      title: "Finding Denver's up-and-coming neighborhoods",
      description:
        "Score every Denver block group on 2025 activity, destination share, momentum (Jul–Nov 2025 vs. Jul–Nov 2024), summer index and audience, combine them with explicit weights, and stress-test the shortlist, in Python.",
      tool: "python",
      country: "USA",
      tags: ["scoring", "screening", "neighborhoods", "cbg", "denver"],
      datasets: [
        "neighborhood_visitation.foot_traffic_month",
        "neighborhood_visitation.census_visitor_demographics",
        "neighborhood_visitation.census_block_groups",
      ],
    },
  ],
  "store-profiling": [
    {
      slug: "us-heb-san-antonio-store-profile",
      title: "Profiling an H-E-B store in San Antonio",
      description:
        "One store, full picture in lean Python: trend vs. chain, competitors and category, home and work catchment, audience, journeys, and the weekly rhythm.",
      tool: "python",
      country: "USA",
      tags: ["store-profile", "catchment", "trade-area", "visitor-journey", "popular-times", "san-antonio"],
      datasets: [
        "visitation_usa_places.places",
        "visitation_usa_places.foot_traffic_month",
        "visitation_usa_places.trade_area_zip",
        "visitation_usa_places.spatialai_visitor_profile",
        "visitation_usa_places.visitor_journey",
        "visitation_usa_places.popular_times",
      ],
    },
    // PARKED: the two visitor journey heatmap examples are withheld while we
    // are not promoting Visitor Journey (expensive to serve). Their bodies stay
    // in example-bodies/, unreferenced; see the note at the top of
    // example-bodies/index.ts for how to bring them back.
  ],
  "brand-comparison": [
    {
      slug: "us-how-far-visitors-travel",
      title: "Measuring how far visitors travel to Buc-ee's, Costco, and three other chains",
      description:
        "ZIP home trade areas turned into a distance profile in Python: per store, the share of visitors living over 25, 50, and 100 miles away, averaged into a chain figure with coverage and store-count guardrails.",
      tool: "python",
      country: "USA",
      tags: ["trade-area", "catchment", "distance", "road-trip", "brands"],
      datasets: ["visitation_usa_places.trade_area_zip"],
    },
    {
      slug: "us-grocery-brand-comparison",
      title: "Grocery brand comparison",
      description:
        "Four grocery chains head to head in Node.js: overall vs. typical store, same-store development with the share of stores growing, and the audience fingerprint.",
      tool: "node",
      country: "USA",
      tags: ["benchmarking", "grocery", "brands", "demographics"],
      datasets: [
        "visitation_usa_places.foot_traffic_month",
        "visitation_usa_places.spatialai_visitor_profile",
      ],
    },
  ],
  "match-locations": [
    {
      slug: "match-addresses-api",
      title: "Matching a store list to location identifiers",
      description:
        "Four San Antonio addresses matched in Python: exact address, disambiguation by brand, a value search for the one the list misspelled, and the join the mapping enables.",
      tool: "python",
      country: "USA",
      tags: ["matching", "placekey", "custom-locations", "san-antonio"],
      datasets: [
        "visitation_usa_places.places",
        "visitation_usa_places.foot_traffic_month",
      ],
    },
  ],
  "custom-boundaries-api": [
    {
      slug: "submit-custom-polygon",
      title: "Submit a custom polygon and get visitor counts",
      description:
        "Nullam quis ante. Etiam sit amet orci eget eros faucibus tincidunt. Duis leo. Sed fringilla mauris.",
      tool: "api",
    },
    {
      slug: "norwegian-metric-models",
      title: "Get visitors against Norwegian metric models",
      description:
        "Donec mi odio, faucibus at, scelerisque quis, convallis in, nisi. Suspendisse non nisl sit amet velit hendrerit.",
      tool: "api",
      country: "NOR",
    },
  ],
  "boundary-management": [
    {
      slug: "create-area-console",
      title: "Create a custom area in Console",
      description:
        "Cras non dolor. Vivamus in erat ut urna cursus vestibulum. Aenean fermentum, elit eget tincidunt condimentum.",
      tool: "console",
    },
  ],
  "custom-odm": [
    {
      slug: "time-of-day-breakdown",
      title: "Configure a custom ODM with time-of-day breakdowns",
      description:
        "Pellentesque commodo eros a enim. Vestibulum turpis sem, aliquet eget, lobortis pellentesque, rutrum eu, nisl.",
      tool: "api",
    },
  ],
}

/**
 * Single visibility choke point. Lifecycle semantics beyond published/draft
 * (in-review, archived) are CAT-1037 territory — extend here when defined.
 */
export const isLive = (g: GuideEntry): boolean => g.status === "published"

export function getGuide(slug: string): GuideEntry | undefined {
  return guides.find((g) => g.slug === slug)
}

/** Guides tagged with a given surface tag (e.g. "get-started", "api"), published only. */
export function guidesTagged(tag: string): GuideEntry[] {
  return guides.filter((g) => g.tags.includes(tag) && g.status === "published")
}

// Append: case studies (sourced from Crowd Insights customer documentation) and
// API-shaped guides for custom workflows. Slugs prefixed `case-study-` so they're
// easy to filter in admin tooling later.
guides.push(
  {
    slug: "case-study-fintraffic-road-network",
    title: "Fintraffic: smarter road traffic management with telco insights",
    description:
      "Fintraffic uses Crowd Insights to deliver more precise road-specific traffic insights for planning, maintenance, and investment decisions across Finland, reducing reliance on traditional roadside counts.",
    status: "draft",
    industry: ["municipalities"],
    locationTypes: ["way", "area"],
    tags: ["case-study", "transportation", "infrastructure"],
    relatedDatasets: [],
    relatedMetrics: ["traffic", "origin-destination-way"],
  },
  {
    slug: "case-study-public-transport-planning",
    title: "Routing better bus service from where passengers actually want to go",
    description:
      "Crowd Insights reveals where bus-corridor passengers actually want to travel, beyond what onboard counts can show, supporting service adjustments that better match demand and capacity allocation.",
    status: "draft",
    industry: ["municipalities"],
    locationTypes: ["way", "area"],
    tags: ["case-study", "transportation", "od-matrix"],
    relatedDatasets: [],
    relatedMetrics: ["origin-destination", "origin-destination-way"],
  },
  {
    slug: "case-study-trafikanalys-travel-patterns",
    title: "Trafikanalys: describing travel behavior with movement data + surveys",
    description:
      "Trafikanalys combines Crowd Insights with survey data to describe travel behavior in more detail, giving authorities a stronger basis for understanding how people move and producing more robust travel pattern analysis.",
    status: "draft",
    industry: ["municipalities"],
    locationTypes: ["area", "way"],
    tags: ["case-study", "transportation", "research"],
    relatedDatasets: [],
    relatedMetrics: ["origin-destination", "foot-traffic-vwr"],
  },
  {
    slug: "case-study-infrastructure-demand-planning",
    title: "Designing infrastructure that responds to local demand",
    description:
      "Crowd Insights surfaces how movement patterns and population variations affect demand across locations, giving planners a stronger basis for designing efficient and sustainable infrastructure.",
    status: "draft",
    industry: ["municipalities"],
    locationTypes: ["area", "way"],
    tags: ["case-study", "infrastructure", "planning"],
    relatedDatasets: [],
    relatedMetrics: ["foot-traffic-vwr", "origin-destination"],
  },
  {
    slug: "case-study-event-visitor-insights",
    title: "Measuring event impact with visitor flow analysis",
    description:
      "Crowd Insights analyzes visitor flows before, during, and after an event, including where attendees come from, how long they stay, and where they go next, for organizers, venues, and destinations evaluating event impact.",
    status: "draft",
    industry: [],
    locationTypes: ["place", "area"],
    tags: ["case-study", "events", "visitor-flow"],
    relatedDatasets: [],
    relatedMetrics: ["foot-traffic-vwr", "visit-length-area", "trade-area-area"],
  },
  {
    slug: "case-study-scb-tourism-seasonal-shifts",
    title: "SCB: measuring seasonal population shifts at attractions and events",
    description:
      "Statistics Sweden (SCB) uses Crowd Insights to show how festivals, sports events, markets, beaches, and other attractions affect movement between places during the summer, informing destination development and public planning.",
    status: "draft",
    industry: ["municipalities"],
    locationTypes: ["area"],
    tags: ["case-study", "tourism", "seasonality"],
    relatedDatasets: [],
    relatedMetrics: ["foot-traffic-vwr", "origin-destination"],
  },
  {
    slug: "case-study-tourism-insights",
    title: "Tourism organizations turn movement data into actionable insights",
    description:
      "Crowd Insights helps tourism organizations understand how visitors travel, where they come from, and how destinations are used, supporting better planning, destination development, and data-driven decisions.",
    status: "draft",
    industry: ["municipalities"],
    locationTypes: ["area", "place"],
    tags: ["case-study", "tourism"],
    relatedDatasets: [],
    relatedMetrics: ["foot-traffic-vwr", "trade-area-area", "visitor-profile-area"],
  },
  {
    slug: "case-study-scb-mobility-segregation",
    title: "SCB: analyzing urban segregation through daily mobility patterns",
    description:
      "SCB uses Crowd Insights to reveal how daily travel patterns differ between areas and population groups, supporting analysis of how segregation is reflected in everyday movement and informing social and urban planning.",
    status: "draft",
    industry: ["municipalities"],
    locationTypes: ["area", "way"],
    tags: ["case-study", "research", "mobility"],
    relatedDatasets: [],
    relatedMetrics: ["origin-destination", "foot-traffic-vwr"],
  },
  // API-shaped guides for custom workflows.
  {
    slug: "custom-boundaries-api",
    title: "Generate metrics for your own polygons with the Custom Boundaries API",
    description:
      "Submit your own polygons as GeoJSON and receive foot traffic, popular times, trade areas, visitor demographics and visitor journeys on demand. Available through the Python client and gRPC; up to 500 locations per report. See Custom Locations in the API reference for examples.",
    status: "draft",
    industry: ["retail", "real-estate", "municipalities"],
    locationTypes: ["place", "area"],
    tags: ["platform", "api", "custom-locations", "byo"],
    relatedDatasets: [],
    relatedMetrics: ["foot-traffic-vwr", "trade-area-area", "visitor-journey"],
  },
  {
    slug: "boundary-management",
    title: "Manage custom boundaries in the Console",
    description:
      "Create, edit, or upload geographic boundaries in the Pine59 Console and attach metadata. Export them as GeoJSON to use with the Custom Boundaries API.",
    status: "draft",
    industry: ["retail", "real-estate", "municipalities"],
    locationTypes: ["place", "area"],
    tags: ["platform", "console", "custom-locations", "no-code"],
    relatedDatasets: [],
    relatedMetrics: [],
  },
  {
    slug: "custom-odm",
    title: "Configure a Custom Origin-Destination Matrix",
    description:
      "Beyond the standard ODM products: filter trips that pass through specific areas, define custom time-of-day bands, alter trip definitions, and surface speed distributions. Currently delivered via specification form (API delivery planned).",
    status: "draft",
    industry: ["municipalities"],
    locationTypes: ["area", "way"],
    tags: ["platform", "api", "custom-locations", "od-matrix"],
    relatedDatasets: [],
    relatedMetrics: ["origin-destination", "origin-destination-way"],
  },
)

// Attach examples at module init — runs after `guides.push(...)` so case-study
// and custom-workflow guides also get their examples.
guides.forEach((g) => {
  const ex = EXAMPLES_BY_GUIDE[g.slug]
  if (ex && ex.length > 0) g.examples = ex
})
